<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue'
import { useOpsStore } from '@/stores/ops'
import { useTeamsStore } from '@/stores/teams'
import { usePersonnelStore } from '@/stores/personnel'
import { useAssignmentsStore } from '@/stores/assignments'
import { usePermissionsStore } from '@/stores/permissions'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import { useToastStore } from '@/stores/toast'
import { AssignmentStatus } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosDateInput from '@/components/ui/FosDateInput.vue'
import OPSelector from '@/components/ops/OPSelector.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

const incidentId = inject<string>('incidentId')!
const opsStore = useOpsStore()
const teamsStore = useTeamsStore()
const personnelStore = usePersonnelStore()
const assignmentsStore = useAssignmentsStore()
const permissionsStore = usePermissionsStore()
const { opRole } = useCurrentOpRole()
const toast = useToastStore()

const canCreateOp = computed(() => permissionsStore.can('op.create', opRole.value))

watch(
  () => opsStore.selectedOpId,
  (opId) => {
    if (opId) assignmentsStore.subscribeToAssignments(incidentId, opId)
  },
  { immediate: true },
)

const teamCount = computed(() => teamsStore.teams.length)
const personnelOnScene = computed(() => personnelStore.onSceneCount)
const activeAssignments = computed(
  () => assignmentsStore.assignments.filter((a) => a.assignment_status === AssignmentStatus.InProgress).length,
)

const showNewOpDialog = ref(false)
const creating = ref(false)
const newOpDate = ref<string>(new Date().toLocaleDateString('en-CA'))

function openNewOpDialog() {
  newOpDate.value = new Date().toLocaleDateString('en-CA')
  showNewOpDialog.value = true
}

async function confirmCreateOp() {
  if (!newOpDate.value) return
  creating.value = true
  try {
    const opId = await opsStore.createOp(incidentId, { date: newOpDate.value })
    opsStore.selectOp(opId)
    showNewOpDialog.value = false
    toast.add('Operational Period created', 'success')
  } catch {
    toast.add('Failed to create Operational Period', 'error')
  } finally {
    creating.value = false
  }
}
</script>

<template>
  <FosDialog
    :open="showNewOpDialog"
    title="New Operational Period"
    max-width="400px"
    @update:open="showNewOpDialog = $event"
  >
    <div class="flex flex-col gap-4 py-2">
      <p class="text-sm text-on-surface-variant">
        A new OP will be created and auto-numbered. This cannot be undone.
      </p>
      <div class="flex flex-col gap-1">
        <label class="text-[10px] text-outline font-label uppercase tracking-widest">Date</label>
        <FosDateInput v-model="newOpDate" />
      </div>
    </div>
    <template #footer>
      <FosButton variant="secondary" :disabled="creating" @click="showNewOpDialog = false">Cancel</FosButton>
      <FosButton variant="primary" :disabled="creating || !newOpDate" @click="confirmCreateOp">
        <span v-if="creating" class="w-4 h-4 border-2 border-outline/30 border-t-outline animate-spin inline-block" />
        Create OP
      </FosButton>
    </template>
  </FosDialog>

  <div class="h-full flex flex-col overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
      <RouterLink
        :to="{ name: 'incident-settings', params: { id: incidentId } }"
        class="text-[10px] text-outline font-label uppercase tracking-widest hover:text-on-surface-variant transition-colors flex items-center gap-1"
      >
        <span class="material-symbols-outlined text-[14px]">arrow_back</span>
        Back to Settings
      </RouterLink>
      <PermissionGuard v-if="canCreateOp" action="op.create" :role="opRole">
        <FosButton variant="primary" @click="openNewOpDialog">
          <span class="material-symbols-outlined text-[16px]">add</span>
          New Operational Period
        </FosButton>
      </PermissionGuard>
    </div>

    <!-- OP Selector -->
    <div class="px-6 py-4 shrink-0">
      <OPSelector
        :model-value="opsStore.selectedOpId"
        :ops="opsStore.ops"
        @update:model-value="opsStore.selectOp($event)"
      />
    </div>

    <!-- Stats -->
    <div v-if="opsStore.selectedOpId" class="grid grid-cols-1 sm:grid-cols-3 gap-4 px-6 pb-4 shrink-0">
      <div class="bg-surface-container border border-border p-5 space-y-1">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest">Teams</p>
        <p class="text-3xl font-bold font-mono text-on-surface">{{ teamCount }}</p>
      </div>
      <div class="bg-surface-container border border-border p-5 space-y-1">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest">Personnel on Scene</p>
        <p class="text-3xl font-bold font-mono text-on-surface">{{ personnelOnScene }}</p>
      </div>
      <div class="bg-surface-container border border-border p-5 space-y-1">
        <p class="text-[10px] text-outline font-label uppercase tracking-widest">Active Assignments</p>
        <p class="text-3xl font-bold font-mono text-on-surface">{{ activeAssignments }}</p>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="opsStore.ops.length === 0"
      class="bg-surface-container-low px-6 py-16 text-center"
    >
      <p class="text-outline">No operational periods have been created yet.</p>
      <PermissionGuard action="op.create" :role="opRole">
        <FosButton variant="primary" class="mt-4" @click="openNewOpDialog">
          <span class="material-symbols-outlined text-[16px]">add</span>
          Create First OP
        </FosButton>
      </PermissionGuard>
    </div>
  </div>
</template>
